/* eslint-disable complexity */
import { Checkbox, Form } from 'antd';
import dayjs from 'dayjs';
import Image from 'next/image';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiOutlineCalendar,
  AiOutlineEnvironment,
  AiOutlineHome,
  AiOutlineLock,
  AiOutlineMail,
  AiOutlineUser,
} from 'react-icons/ai';

import cloudinaryUrl from '@/utils/cloudinary';
import { CARD_NUMBER_PATTERN, CVC_PATTERN } from '@/utils/card';
import { getCurrencySymbol } from '@/utils/currency';
import { BookingCost, Location } from '@core/booking/booking.types';
import Dropdown from 'common/atoms/dropdown';
import Input from 'common/atoms/input';

import NoAvailablePayment from '../payment-info-form/NoAvailablePayment';
import PersonalInfoForm from '../personal-info-form';

import { DayPassInfoState } from './BookingCard';
import { DayPassSettings } from './ConfigureStep';

const MAX_DATES_COLLAPSED = 3;

interface DayPassCheckoutProps {
  dayPassSettings: DayPassSettings | null;
  location: Location | null;
  isReserved: boolean;
  dayPassInfo: DayPassInfoState;
  dayCount: number;
  officeName: string | null;
  personalInfo: { name: string; email: string };
  bookingCost: BookingCost | null;
  // 'info' = mobile step 1 (your details), 'payment' = mobile step 2.
  // Desktop always renders the full two-column checkout regardless.
  mobileStage: 'info' | 'payment';
  form: any;
  paymentInfo: any;
  handlePersonalInfoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaymentInfoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaymentTypeChange: (value: string) => void;
  availablePaymentMethods: string[];
  hideZipAndAddress: boolean;
  onPay: () => void;
  loading: boolean;
}

/**
 * Checkout screen of the day-pass journey — order summary alongside details
 * and payment. Adapted from the meeting-room CheckoutStep; day-pass costs
 * have no discount-code support, so that block is intentionally absent.
 */
const DayPassCheckout: FC<DayPassCheckoutProps> = ({
  dayPassSettings,
  location,
  isReserved,
  dayPassInfo,
  dayCount,
  officeName,
  personalInfo,
  bookingCost,
  mobileStage,
  form,
  paymentInfo,
  handlePersonalInfoInputChange,
  handlePaymentInfoInputChange,
  handlePaymentTypeChange,
  availablePaymentMethods,
  hideZipAndAddress,
  onPay,
  loading,
}) => {
  const { t } = useTranslation();
  const [datesExpanded, setDatesExpanded] = useState(false);
  const hasCreditCard = availablePaymentMethods.find((method) => method === 'credit_card') !== undefined;

  const title = dayPassSettings?.title || t('bookingSteps.bookDaypass');
  const imageUrl = dayPassSettings?.value ? cloudinaryUrl(dayPassSettings.value, 500) : '/placeholder.webp';
  const currencySymbol = getCurrencySymbol(bookingCost?.currency, location);
  const hasCost = Number(bookingCost?.total) > 0;
  const totalText = hasCost ? `${currencySymbol}${bookingCost!.total.toFixed(2)}` : '';
  const sortedDates = [...dayPassInfo.dates].sort();

  if (!hasCreditCard) {
    return <NoAvailablePayment />;
  }

  let reservedRangeText = '';
  if (dayPassInfo.startDate && dayPassInfo.endDate) {
    const rangeLabel = `${dayjs(dayPassInfo.startDate).format('MMM D')} – ${dayjs(dayPassInfo.endDate).format(
      'MMM D, YYYY',
    )}`;
    reservedRangeText = `${rangeLabel} · ${t('dayPass.daysCount', { count: dayCount })}`;
  }

  const dateSummary = isReserved ? (
    <span className="text-sm" style={{ color: '#404D61' }}>
      {reservedRangeText}
    </span>
  ) : (
    <div className="min-w-0">
      <span className="text-sm" style={{ color: '#404D61' }}>
        {t('dayPass.daysSelected', { count: dayCount })}
        {': '}
        {sortedDates
          .slice(0, MAX_DATES_COLLAPSED)
          .map((d) => dayjs(d).format('MMM D'))
          .join(' · ')}
      </span>
      {sortedDates.length > MAX_DATES_COLLAPSED && !datesExpanded && (
        <button
          type="button"
          className="text-xs font-medium bg-transparent border-none p-0 ms-1 cursor-pointer"
          style={{ color: 'var(--main)' }}
          onClick={() => setDatesExpanded(true)}
        >
          {t('dayPass.moreDates', { count: sortedDates.length - MAX_DATES_COLLAPSED })}
        </button>
      )}
      {datesExpanded && (
        <div className="ps-6 mt-1">
          {sortedDates.map((d) => (
            <p key={d} className="text-sm m-0" style={{ color: '#404D61' }}>
              {dayjs(d).format('ddd, MMM D, YYYY')}
            </p>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row gap-6 p-2 md:p-0">
      {/* Order Summary — hidden on the mobile "your info" stage */}
      <div
        className={`${
          mobileStage === 'payment' ? '' : 'hidden md:block'
        } w-full md:w-96 flex-shrink-0 rounded-2xl p-6 md:sticky md:top-6 md:self-start`}
        style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB' }}
      >
        <h3 className="text-base font-bold m-0 mb-4" style={{ color: '#404D61' }}>
          {t('meetingRoom.yourBooking')}
        </h3>

        <div className="relative w-full overflow-hidden rounded-xl mb-4" style={{ height: 160 }}>
          <Image src={imageUrl} alt={title} fill style={{ objectFit: 'cover' }} unoptimized />
        </div>

        <h4 className="text-lg font-bold m-0 mb-1" style={{ color: '#404D61' }}>
          {title}
        </h4>
        {location && (
          <div className="flex items-center gap-1 mb-4" style={{ color: '#707070' }}>
            <AiOutlineEnvironment size={14} />
            <span className="text-sm">
              {location.name}
              {location.address ? ` · ${location.address}` : ''}
            </span>
          </div>
        )}

        <div
          className="space-y-2 mb-4 py-4"
          style={{ borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}
        >
          <div className="flex items-start gap-2">
            <AiOutlineCalendar size={16} style={{ color: 'var(--main)', flexShrink: 0, marginTop: 2 }} />
            {dateSummary}
          </div>
          {isReserved && officeName && (
            <div className="flex items-center gap-2">
              <AiOutlineHome size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm" style={{ color: '#404D61' }}>
                {officeName}
              </span>
            </div>
          )}
          {personalInfo.name && (
            <div className="flex items-center gap-2">
              <AiOutlineUser size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm" style={{ color: '#404D61' }}>
                {personalInfo.name}
              </span>
            </div>
          )}
          {personalInfo.email && (
            <div className="flex items-center gap-2">
              <AiOutlineMail size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm" style={{ color: '#404D61' }}>
                {personalInfo.email}
              </span>
            </div>
          )}
        </div>

        {hasCost && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: '#707070' }}>{t('meetingRoom.summary.subtotal')}</span>
              <span className="font-medium" style={{ color: '#404D61' }} dir="ltr">
                {currencySymbol}
                {bookingCost!.subTotal.toFixed(2)}
              </span>
            </div>
            {bookingCost!.vat > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: '#707070' }}>{t('meetingRoom.summary.vat', { percent: bookingCost!.vat })}</span>
                <span className="font-medium" style={{ color: '#707070' }} dir="ltr">
                  {currencySymbol}
                  {(bookingCost!.total - bookingCost!.subTotal).toFixed(2)}
                </span>
              </div>
            )}
            <div
              className="flex justify-between text-lg font-bold mt-2 pt-2"
              style={{ borderTop: '1px solid #E5E7EB' }}
            >
              <span style={{ color: '#404D61' }}>{t('meetingRoom.summary.total')}</span>
              <span style={{ color: 'var(--main)' }} dir="ltr">
                {currencySymbol}
                {bookingCost!.total.toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Details + Payment */}
      <div className="flex-1 min-w-0">
        {/* Mobile recap strip — "your info" stage only */}
        {mobileStage === 'info' && (
          <div
            className="md:hidden rounded-xl p-3 mb-4 flex justify-between items-center text-sm"
            style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB' }}
          >
            <div className="min-w-0">
              <span className="font-medium" style={{ color: '#404D61' }}>
                {title}
              </span>
              <span className="ms-2" style={{ color: '#707070' }}>
                {t('dayPass.daysCount', { count: dayCount })}
              </span>
            </div>
            {hasCost && (
              <span className="font-bold" style={{ color: 'var(--main)' }} dir="ltr">
                {currencySymbol}
                {bookingCost!.total.toFixed(2)}
              </span>
            )}
          </div>
        )}

        <Form form={form} layout="vertical" className="w-full">
          {/* Your details — mobile shows on the "info" stage; desktop always */}
          <div className={mobileStage === 'info' ? '' : 'hidden md:block'}>
            <h3 className="text-base font-bold m-0 mb-3" style={{ color: '#404D61' }}>
              {t('bookingSteps.yourDetails')}
            </h3>
            <PersonalInfoForm
              form={form}
              personalInfo={personalInfo}
              handlePersonalInfoInputChange={handlePersonalInfoInputChange}
              compact
              noFormWrapper
            />
            <div className="my-4 hidden md:block" style={{ borderTop: '1px solid #E5E7EB' }} />
          </div>

          {/* Payment — mobile shows on the "payment" stage; desktop always */}
          <div className={mobileStage === 'payment' ? '' : 'hidden md:block'}>
            <h3 className="text-base font-bold m-0 mb-3" style={{ color: '#404D61' }}>
              {t('bookingSteps.paymentDetails')}
            </h3>

            <Form.Item
              initialValue={paymentInfo.paymentType}
              name="paymentType"
              label={t('payment.paymentType')}
              rules={[{ required: true, message: t('payment.validation.paymentTypeRequired') }]}
              style={{ marginBottom: 16 }}
            >
              <Dropdown
                onChange={handlePaymentTypeChange}
                options={[{ value: 'credit_card', label: t('payment.creditCard') }]}
                name={t('payment.paymentType')}
              />
            </Form.Item>

            <Form.Item
              initialValue={paymentInfo.cardNumber}
              name="cardNumber"
              label={t('payment.cardNumber')}
              rules={[
                { required: true, message: t('payment.validation.cardNumberRequired') },
                {
                  pattern: CARD_NUMBER_PATTERN,
                  message: t('payment.validation.invalidCard'),
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                onChange={handlePaymentInfoInputChange}
                name="cardNumber"
                placeholder={t('payment.placeholders.cardNumber')}
              />
            </Form.Item>

            <Form.Item
              initialValue={paymentInfo.nameOnTheCard}
              name="nameOnTheCard"
              label={t('payment.nameOnCard')}
              rules={[
                { required: true, message: t('payment.validation.nameRequired') },
                { pattern: /^[\p{L}\p{M}\s'’.-]{2,30}$/u, message: t('payment.validation.nameInvalid') },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                onChange={handlePaymentInfoInputChange}
                name="nameOnTheCard"
                placeholder={t('payment.nameOnCard')}
              />
            </Form.Item>

            <div className="flex gap-4">
              <Form.Item label={t('payment.expirationDate')} required style={{ marginBottom: 16, flex: 1 }}>
                <div className="flex items-center gap-2" dir="ltr">
                  <Form.Item
                    initialValue={paymentInfo.expireDateMonth}
                    name="expireDateMonth"
                    noStyle
                    rules={[
                      { required: true, message: t('payment.validation.monthRequired') },
                      { pattern: /^(0[1-9]|1[0-2])$/, message: t('payment.validation.month') },
                    ]}
                  >
                    <Input
                      onChange={handlePaymentInfoInputChange}
                      name="expireDateMonth"
                      placeholder={t('payment.placeholders.mm')}
                    />
                  </Form.Item>
                  <span style={{ color: '#9CA3AF' }}>/</span>
                  <Form.Item
                    initialValue={paymentInfo.expireDateYear}
                    name="expireDateYear"
                    noStyle
                    rules={[
                      { required: true, message: t('payment.validation.yearRequired') },
                      { pattern: /^(2[3-9]|3[0-9])$/, message: t('payment.validation.year') },
                    ]}
                  >
                    <Input
                      onChange={handlePaymentInfoInputChange}
                      name="expireDateYear"
                      placeholder={t('payment.placeholders.yy')}
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                initialValue={paymentInfo.cvv}
                name="cvc_cvv"
                label={t('payment.cvc')}
                rules={[
                  { required: true, message: t('common.required') },
                  { pattern: CVC_PATTERN, message: t('payment.validation.cvcInvalid') },
                ]}
                style={{ marginBottom: 16, width: 120 }}
              >
                <Input onChange={handlePaymentInfoInputChange} name="cvv" placeholder={t('payment.placeholders.cvc')} />
              </Form.Item>
            </div>

            {!hideZipAndAddress && (
              <Form.Item
                initialValue={paymentInfo.address}
                name="billingAddress"
                label={t('payment.billingAddress')}
                rules={[{ required: true, message: t('payment.validation.billingAddressRequired') }]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  onChange={handlePaymentInfoInputChange}
                  name="address"
                  placeholder={t('payment.billingAddress')}
                />
              </Form.Item>
            )}

            {!hideZipAndAddress && (
              <Form.Item
                initialValue={paymentInfo.zip}
                name="ZIP"
                label={t('payment.zipCode')}
                rules={[
                  { required: true, message: t('payment.validation.zipRequired') },
                  { pattern: /^\d{5}$/, message: t('payment.validation.zipInvalid') },
                ]}
                style={{ marginBottom: 16 }}
              >
                <Input onChange={handlePaymentInfoInputChange} name="zip" placeholder={t('payment.placeholders.zip')} />
              </Form.Item>
            )}

            <Checkbox
              checked={paymentInfo.save_cc}
              onChange={(e) => {
                handlePaymentInfoInputChange({ target: { name: 'save_cc', value: e.target.checked } } as any);
              }}
              style={{ marginBottom: 16 }}
            >
              <span className="text-sm" style={{ color: '#404D61' }}>
                {t('payment.saveCard')}
              </span>
            </Checkbox>

            <div className="flex items-center gap-2 mb-6 mt-2">
              <AiOutlineLock size={16} style={{ color: 'var(--main)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--main)' }}>
                {t('payment.tlsNotice')}
              </span>
            </div>

            <button
              type="button"
              onClick={onPay}
              disabled={loading}
              className="w-full py-4 rounded-xl text-white text-base font-semibold border-none cursor-pointer transition-opacity"
              style={{
                backgroundColor: 'var(--main)',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? t('payment.processing') : t('payment.payAmount', { amount: totalText })}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default DayPassCheckout;
